import { forwardRef, type CSSProperties, type ComponentPropsWithoutRef } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from '@vitness/icons';
import { cn, useTheme } from '@vitness/theme';

/** Tri-state value for a {@link Checkbox}: on, off, or partially selected. */
export type CheckedState = boolean | 'indeterminate';

/**
 * Public props for {@link Checkbox}. Authored explicitly (rather than
 * re-exporting Radix's prop type) so the component's API surface stays
 * independent of the Radix version and never leaks Radix types to consumers.
 */
export interface CheckboxProps
  extends Omit<
    ComponentPropsWithoutRef<'button'>,
    'onChange' | 'checked' | 'defaultChecked' | 'value' | 'type'
  > {
  /** Controlled state. Use `'indeterminate'` for partial selection. */
  checked?: CheckedState;
  /** Uncontrolled initial state. */
  defaultChecked?: CheckedState;
  /** Fires when the state changes. */
  onCheckedChange?: (checked: CheckedState) => void;
  /** Whether the checkbox is required in a form. */
  required?: boolean;
  /** Form field name. */
  name?: string;
  /** Form submission value when checked (default `"on"`). */
  value?: string;
}

/**
 * Accent-colored checkbox built on Radix Checkbox — designed for row-selection
 * use cases. Supports the `indeterminate` state (renders a dash) in addition to
 * checked/unchecked. The checked fill uses the active theme accent via the
 * `--vn-control-accent` CSS variable so it tracks every accent in dark/light.
 * Keyboard- and screen-reader-accessible (`role="checkbox"`, Space toggle).
 */
export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(
  { className, style, ...props },
  ref,
) {
  const { accentSafe } = useTheme();

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      style={{ '--vn-control-accent': accentSafe, ...style } as CSSProperties}
      className={cn(
        'group peer inline-flex h-[16px] w-[16px] shrink-0 cursor-pointer items-center justify-center rounded-vn-sm',
        'border border-border-strong bg-surface-inset transition-colors',
        'outline-none focus-visible:ring-2 focus-visible:ring-[var(--vn-control-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-surface-bg',
        'disabled:cursor-not-allowed disabled:opacity-45',
        'data-[state=checked]:border-[var(--vn-control-accent)] data-[state=checked]:bg-[var(--vn-control-accent)]',
        'data-[state=indeterminate]:border-[var(--vn-control-accent)] data-[state=indeterminate]:bg-[var(--vn-control-accent)]',
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-surface-bg">
        <Check
          size={11}
          strokeWidth={3}
          aria-hidden="true"
          className="hidden group-data-[state=checked]:block"
        />
        <Minus
          size={11}
          strokeWidth={3}
          aria-hidden="true"
          className="hidden group-data-[state=indeterminate]:block"
        />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});
